import React, { useState } from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Toggle } from '@components/base'
import { Bugsnag, Device } from '@src/config'
import { healthKitSyncStatusSelector, healthLastSyncedSelector } from '@src/selectors/integrations'
import { openUrl, useDispatchAsync, useSnack } from '@src/utils'
import { NavigationContainer } from '@src/screens/Common/containers'
import { Section } from '@components'
import { useNativeHealthKit } from '../../utils/hooks'
import LastSyncedSection from './LastSyncedSection'

export const HealthKitIntegration = () => {
  const lastSyncedHealth = useSelector(healthLastSyncedSelector)
  const healthKitSync = useSelector(healthKitSyncStatusSelector)
  const styles = useStyleSheet(themedStyles)
  const enableHealthKit = useNativeHealthKit()
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const [saving, setSaving] = useState(false)

  const onToggle = async (enabled: boolean) => {
    if (saving) {
      return
    }
    setSaving(true)
    try {
      if (enabled) {
        const success = await enableHealthKit()
        if (!success) {
          showSnack('Unable to enable Apple Health. Please try again.', null, 'error')
        }
      } else {
        await dispatchAsync({
          type: 'settings/updateHealthKitSync',
          payload: { healthKitSync: false },
        })
      }
    } catch (error: any) {
      Bugsnag.notify(error)
      showSnack('Unable to update Apple Health. Please try again.', null, 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!Device.hasHealthKit) {
    return null
  }

  const healthKitSyncToggle = (
    <Section title="Apple Health Sync">
      <Toggle
        accessibilityLabel="Apple Health Sync"
        checked={healthKitSync}
        disabled={saving}
        onChange={onToggle}
      />
    </Section>
  )

  const healthDataLastSynced = <LastSyncedSection lastSynced={lastSyncedHealth} />

  const title = 'Apple Health'

  const lastSync = healthKitSync && healthDataLastSynced

  const onConfigurePress = () => {
    openUrl('App-Prefs:Privacy&path=HEALTH')
  }

  const configureHealthKitButton = (
    <Button
      type="primary"
      size="block"
      style={styles.configureButton}
      onPress={onConfigurePress}
      accessibilityLabel="Configure"
    >
      Configure
    </Button>
  )

  return (
    <NavigationContainer title={title} showLoadingIndicator>
      <View style={styles.container}>
        {healthKitSyncToggle}
        {lastSync}
        {healthKitSync && configureHealthKitButton}
      </View>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    flexGrow: 1,
    backgroundColor: 'theme.background',
  },
  configureButton: {
    marginTop: 'auto',
    marginBottom: 24,
    marginHorizontal: 16,
  },
})
